import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  fetchAdminTranslations,
  patchAdminTranslation,
} from '@/api/(admin)/translations';
import {
  type PatchAdminTranslationBody,
  TRANSLATION_LOCALES,
  type TranslationLocale,
} from '@/api/(admin)/translations/types';
import { useTranslator } from '@/hooks/useTranslator';
import { reloadTranslations } from '@/lib/utils/i18n';

const splitId = (id: string) => {
  const separator = id.indexOf(':');
  return { namespace: id.slice(0, separator), key: id.slice(separator + 1) };
};

export function useTranslationKeyEditor(id: string) {
  const { getTranslation: t } = useTranslator({ page: 'translationInspector' });
  const queryClient = useQueryClient();
  const { namespace, key } = splitId(id);
  const queryKey = ['translation-inspector', id];

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      // The search also matches longer keys sharing the prefix.
      const qs = new URLSearchParams({
        page: '1',
        count: '50',
        namespace,
        search: id,
      });
      const { data } = await fetchAdminTranslations(qs.toString());
      return data.find(row => row.key === key) ?? null;
    },
  });
  const translation = query.data ?? null;

  const [draft, setDraft] = useState<
    Partial<Record<TranslationLocale, string>>
  >({});
  const textFor = (locale: TranslationLocale) =>
    draft[locale] ?? translation?.values[locale] ?? '';

  /** Changed locales only; a cleared translation falls back to English. */
  const changes: PatchAdminTranslationBody['values'] = {};
  for (const locale of TRANSLATION_LOCALES) {
    const text = draft[locale]?.trim();
    if (text === undefined || !translation) continue;
    if (text === (translation.values[locale] ?? '')) continue;
    if (locale === 'en') {
      if (text) changes.en = text;
    } else {
      changes[locale] = text || null;
    }
  }

  const save = useMutation({
    mutationFn: () =>
      patchAdminTranslation(namespace, key, { values: changes }),
    onSuccess: async () => {
      setDraft({});
      await queryClient.invalidateQueries({ queryKey });
      // The page behind the panel shows the new text at once.
      await reloadTranslations();
      toast.success(t('panel.saved'));
    },
    onError: error =>
      toast.error(error instanceof Error ? error.message : t('panel.failed')),
  });

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(id);
      toast.success(t('panel.copied'));
    } catch {
      toast.error(t('panel.copyFailed'));
    }
  }

  return {
    translation,
    isLoading: query.isLoading,
    isError: query.isError,
    textFor,
    setValue: (locale: TranslationLocale, text: string) =>
      setDraft(current => ({ ...current, [locale]: text })),
    canSave: Object.keys(changes).length > 0 && !save.isPending,
    save: () => save.mutate(),
    copy,
  };
}
